import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import {
    isSceneDurationUserOverridden,
    setSceneDurationUserOverride,
} from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { isVoiceDrivenSceneGroup, saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { calculateAndSetRecommendedSceneDurationOperation } from 'js/src/luminary/luminary-operations';

export const setSceneDurationSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    mode: z
        .enum(['auto', 'manual'])
        .describe(
            'Use "auto" to calculate duration based on scene text (recommended), or "manual" to set a specific duration',
        ),
    duration: z
        .number()
        .min(1)
        .optional()
        .describe(
            'The duration in milliseconds (required when mode is "manual", ignored when mode is "auto")',
        ),
});
export type SetSceneDurationArgs = z.infer<typeof setSceneDurationSchema>;

export class SetSceneDurationTool extends VideoStateTool<typeof setSceneDurationSchema> {
    protected override readonly schema = setSceneDurationSchema;
    protected override readonly toolName = TOOL_NAMES.setSceneDuration;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Set the duration of a specific scene. Use mode "auto" to automatically calculate the optimal duration based on the text content, or mode "manual" to set a specific duration in milliseconds.';

    public override getToolDisplayName(): string {
        return 'Setting scene duration';
    }

    public get statusIcon(): string {
        return 'timer';
    }

    public async execute(args: SetSceneDurationArgs, context: ToolExecutionContext) {
        const { duration, mode = 'manual' } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            // Check if the scene is voice-driven (connected to transcript)
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene ${sceneShortId}`;
            }
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                if (mode === 'auto') {
                    return `No change: scene ${sceneShortId} is voice-driven, so its duration already follows its words. To change it, change which words it covers with ${TOOL_NAMES.setSceneRange}.`;
                }
                return `Error: Cannot edit duration for voice-driven scenes. Scene ${sceneShortId} is connected to a transcript (has media or voiceover asset).`;
            }

            // Check if the scene is the branding scene (cannot be edited)
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                return `Error: Cannot edit duration for branding scenes. Scene ${sceneShortId} is a branding scene.`;
            }

            const wasOverridden = isSceneDurationUserOverridden(this.getState(), scene.id);

            if (mode === 'auto') {
                const previousDuration = scene.duration;

                // Returning to auto means clearing any existing override first —
                // otherwise the recommended-duration operation bails out early on an
                // overridden scene and this would be a no-op.
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                const { calculateAndSetRecommendedSceneDuration } = makeOperationsCallable(
                    {
                        calculateAndSetRecommendedSceneDurationOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    () => this.getState(),
                );

                calculateAndSetRecommendedSceneDuration(scene.id, false, parentActionGroup);

                const newDuration = scene.duration;
                if (newDuration === previousDuration) {
                    return wasOverridden
                        ? `Success: Switched scene ${sceneShortId} back to automatic duration; it is already ${newDuration} milliseconds, so the length is unchanged.`
                        : `No change: scene ${sceneShortId} is already automatic at ${newDuration} milliseconds.`;
                }
                return `Success: Automatically set duration for scene ${sceneShortId} to ${newDuration} milliseconds (was ${previousDuration}ms).`;
            }

            // Manual mode
            if (duration === undefined || duration <= 0) {
                return 'Error: duration must be a positive number when using manual mode';
            }

            if (scene.duration === duration) {
                if (wasOverridden) {
                    return `No change: scene ${sceneShortId} is already ${duration} milliseconds.`;
                }
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                return `Success: Scene ${sceneShortId} was already ${duration} milliseconds; locked it so later text edits won't resize it.`;
            }

            try {
                scene.duration = duration;

                // Save changes to enable undo/redo functionality
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                // A deliberately-set duration must stick: mark it overridden so later
                // text edits don't auto-resize it back.
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                return `Success: Set duration for scene ${sceneShortId} to ${duration} milliseconds.`;
            } catch (error) {
                return `Error: Failed to set duration for scene ${sceneShortId}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting scene duration: ${errorMessage}`;
        }
    }
}
